import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import { API } from '../config';
import '../styles/theme.css';
import './MyApplications.css';

const STATUS_CONFIG = {
  pending:   { cls: 'badge-orange', icon: 'fa-clock',          label: 'Pending Review' },
  applied:   { cls: 'badge-blue',   icon: 'fa-paper-plane',    label: 'Applied' },
  viewed:    { cls: 'badge-purple', icon: 'fa-eye',            label: 'Viewed' },
  interview: { cls: 'badge-green',  icon: 'fa-calendar-check', label: 'Interview' },
  rejected:  { cls: 'badge-red',    icon: 'fa-times-circle',   label: 'Not Selected' },
  accepted:  { cls: 'badge-green',  icon: 'fa-check-circle',   label: 'Accepted' },
  hired:     { cls: 'badge-purple', icon: 'fa-user-tie',       label: 'Hired' },
};

const STATUS_MSG = {
  pending:   'Your application is under review.',
  applied:   'Application submitted successfully.',
  viewed:    'The recruiter has reviewed your application.',
  interview: 'You have been invited for an interview.',
  rejected:  'This role was not a match. Keep applying!',
  accepted:  'Congratulations! Your application was accepted.',
  hired:     'You have been hired for this position!',
};

function StatusTimeline({ status, interviewResponse }) {
  const steps = [
    { key: 'applied',   label: 'Applied',    icon: 'fa-paper-plane' },
    { key: 'interview', label: 'Interview',   icon: 'fa-calendar-check' },
    { key: 'hired',     label: 'Hired',       icon: 'fa-user-tie' },
  ];
  const order = ['pending','applied','viewed','interview','accepted','hired'];
  const currentIdx = order.indexOf(status);
  return (
    <div className="ja-timeline">
      {steps.map((step, i) => {
        const stepIdx = order.indexOf(step.key);
        const done = currentIdx >= stepIdx;
        return (
          <React.Fragment key={step.key}>
            <div className={`ja-timeline-step ${done ? 'done' : ''}`}>
              <div className="ja-timeline-dot"><i className={`fas ${step.icon}`}></i></div>
              <span className="ja-timeline-label">{step.label}</span>
              {step.key === 'interview' && interviewResponse && done && (
                <span className={`badge ja-timeline-sub ${interviewResponse === 'accepted' ? 'badge-green' : interviewResponse === 'declined' ? 'badge-red' : 'badge-orange'}`}>
                  {interviewResponse === 'accepted' ? 'Confirmed' : interviewResponse === 'declined' ? 'Declined' : 'Pending'}
                </span>
              )}
            </div>
            {i < steps.length - 1 && <div className={`ja-timeline-line ${currentIdx > stepIdx ? 'done' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function MyApplications() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [responding, setResponding] = useState(null); // applicationId being responded to

  useEffect(() => { fetchApplications(); }, []);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/applications/my-applications/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const normalized = Array.isArray(data)
        ? data.map(a => ({ ...a, status: a.status?.toLowerCase(), interview_response: a.interview_response?.toLowerCase() }))
        : [];
      setApplications(normalized);
    } catch { setApplications([]); }
    finally { setLoading(false); }
  };

  const [declineConfirm, setDeclineConfirm] = useState(null); // applicationId pending confirmation

  const handleInterviewResponse = async (applicationId, response) => {
    // Show confirmation modal before declining
    if (response === 'declined') {
      setDeclineConfirm(applicationId);
      return;
    }
    await submitInterviewResponse(applicationId, response);
  };

  const submitInterviewResponse = async (applicationId, response) => {
    setResponding(applicationId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/applications/${applicationId}/respond-interview/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ response }),
      });
      if (!res.ok) throw new Error();
      await fetchApplications();
    } catch { alert('Failed to submit response. Please try again.'); }
    finally { setResponding(null); setDeclineConfirm(null); }
  };

  const filtered = filter === 'all' ? applications : applications.filter(a => a.status === filter);
  const counts = applications.reduce((acc, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {});
  const pendingInvites = applications.filter(a => a.status === 'interview' && a.interview_response === 'pending');

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">My Applications</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>My Applications</h1>
            <p>{applications.length} total application{applications.length !== 1 ? 's' : ''}</p>
          </div>

          {/* Pending interview invitations banner */}
          {pendingInvites.length > 0 && (
            <div className="ma-invite-banner">
              <i className="fas fa-bell"></i>
              <span>You have <strong>{pendingInvites.length}</strong> pending interview invitation{pendingInvites.length > 1 ? 's' : ''} — scroll down to respond.</span>
            </div>
          )}

          {/* Stats row */}
          {applications.length > 0 && (
            <div className="stats-row" style={{ marginBottom: '1.5rem' }}>
              {[
                { key: 'applied',   label: 'Applied',    icon: 'fa-paper-plane',    color: 'blue' },
                { key: 'viewed',    label: 'Viewed',     icon: 'fa-eye',            color: 'purple' },
                { key: 'interview', label: 'Interviews', icon: 'fa-calendar-check', color: 'green' },
                { key: 'hired',     label: 'Hired',      icon: 'fa-user-tie',       color: 'green' },
              ].map(s => (
                <div key={s.key} className="stat-card">
                  <div className={`stat-icon-wrap ${s.color}`}><i className={`fas ${s.icon}`}></i></div>
                  <div className="stat-info">
                    <div className="stat-value">{counts[s.key] || 0}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Filter tabs */}
          <div className="apps-filter-tabs">
            {['all', 'pending', 'applied', 'viewed', 'interview', 'hired', 'accepted', 'rejected'].map(tab => (
              <button key={tab} className={`apps-filter-tab ${filter === tab ? 'active' : ''}`} onClick={() => setFilter(tab)}>
                {tab === 'all' ? 'All' : STATUS_CONFIG[tab]?.label}
                {tab !== 'all' && counts[tab] ? <span className="tab-count">{counts[tab]}</span> : null}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="empty-state">
              <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
              <p>Loading applications...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <h3>{filter === 'all' ? 'No applications yet' : `No ${STATUS_CONFIG[filter]?.label} applications`}</h3>
              <p>{filter === 'all' ? 'Start applying to jobs to track your progress here.' : 'Try a different filter.'}</p>
              {filter === 'all' && (
                <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate('/jobs')}>
                  <i className="fas fa-search"></i> Browse Jobs
                </button>
              )}
            </div>
          ) : (
            <div className="apps-list">
              {filtered.map(app => {
                const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
                const hasPendingInvite = app.status === 'interview' && app.interview_response === 'pending';
                const isResponding = responding === app.id;
                return (
                  <div key={app.id} className={`app-card card ${hasPendingInvite ? 'app-card-highlight' : ''}`}>
                    <div className="app-card-header">
                      <div className="app-company-logo">{app.job_company?.charAt(0).toUpperCase() || '?'}</div>
                      <div className="app-card-info">
                        <h3 className="app-job-title">{app.job_title}</h3>
                        <div className="app-job-meta">
                          <span><i className="fas fa-building"></i> {app.job_company}</span>
                          {app.job_location && <span><i className="fas fa-map-marker-alt"></i> {app.job_location}</span>}
                          <span><i className="fas fa-calendar"></i> Applied {new Date(app.applied_at).toLocaleDateString('en-ZA')}</span>
                        </div>
                      </div>
                      <span className={`badge ${cfg.cls}`}><i className={`fas ${cfg.icon}`}></i> {cfg.label}</span>
                    </div>

                    <StatusTimeline status={app.status} interviewResponse={app.interview_response} />

                    <div className="app-status-msg">
                      <i className="fas fa-info-circle"></i>
                      <span>{STATUS_MSG[app.status] || STATUS_MSG.pending}</span>
                    </div>

                    {/* Interview invitation details */}
                    {app.status === 'interview' && app.interview_date && (
                      <div className="app-interview-invite">
                        <div className="app-interview-invite-title">
                          <i className="fas fa-calendar-check"></i> Interview Invitation
                          {hasPendingInvite && <span className="badge badge-orange ma-pulse">Response Required</span>}
                        </div>
                        <div className="app-interview-details">
                          <span><i className="fas fa-calendar-alt"></i> {app.interview_date}</span>
                          <span><i className="fas fa-clock"></i> {app.interview_time}</span>
                          <span><i className="fas fa-map-marker-alt"></i> {app.interview_location}</span>
                        </div>
                        {app.interview_notes && (
                          <p className="app-interview-notes">{app.interview_notes}</p>
                        )}
                        {hasPendingInvite && (
                          <div className="app-interview-actions">
                            <button className="btn btn-sm ja-btn-interview" onClick={() => handleInterviewResponse(app.id, 'accepted')} disabled={isResponding}>
                              <i className="fas fa-check"></i> {isResponding ? 'Saving...' : 'Accept Interview'}
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleInterviewResponse(app.id, 'declined')} disabled={isResponding}>
                              <i className="fas fa-times"></i> Decline
                            </button>
                          </div>
                        )}
                        {app.interview_response === 'accepted' && (
                          <p className="app-interview-confirmed"><i className="fas fa-check-circle"></i> You confirmed this interview.</p>
                        )}
                        {app.interview_response === 'declined' && (
                          <p className="app-interview-declined"><i className="fas fa-times-circle"></i> You declined this interview.</p>
                        )}
                      </div>
                    )}

                    {app.status === 'rejected' && app.rejection_reason && (
                      <div className="app-notes app-notes-rejection">
                        <i className="fas fa-comment"></i>
                        <div><strong>Feedback:</strong> {app.rejection_reason}</div>
                      </div>
                    )}

                    {app.status === 'hired' && (
                      <div className="app-notes app-notes-hired">
                        <i className="fas fa-trophy"></i>
                        <div><strong>Congratulations!</strong> You have been hired for this position.</div>
                      </div>
                    )}

                    <div className="app-card-footer">
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/jobs/${app.job_id}`)}>
                        <i className="fas fa-eye"></i> View Job
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Decline Confirmation Modal */}
      {declineConfirm && (
        <div className="modal-overlay" onClick={() => setDeclineConfirm(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ padding: 0, border: 'none', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i> Decline Interview?
              </h2>
              <button className="modal-close" onClick={() => setDeclineConfirm(null)}><i className="fas fa-times"></i></button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>
                Are you sure you want to decline this interview invitation? The recruiter will be notified and this action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeclineConfirm(null)}>
                <i className="fas fa-arrow-left"></i> Go Back
              </button>
              <button className="btn btn-danger" onClick={() => submitInterviewResponse(declineConfirm, 'declined')} disabled={responding === declineConfirm}>
                <i className={`fas ${responding === declineConfirm ? 'fa-spinner fa-spin' : 'fa-times'}`}></i>
                {responding === declineConfirm ? 'Declining...' : 'Yes, Decline Interview'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyApplications;
